import AppliedApplicationTemplateModel from '../../../../../shared/models/templating/api/AppliedApplicationTemplateModel';
import IResult from '../../../../../shared/models/result/IResult';

export interface FieldGenerateResult {
    fieldTitle: string;
    generatedValue: string;
    appName?: string;
    /** True when a fallback URL retry was attempted for this field but also returned Unknown. */
    retryAttempted?: boolean;
}

export interface AutoGenerateResults {
    successFields: FieldGenerateResult[];
    failedFields: FieldGenerateResult[];
    hasApiError: boolean;
    apiErrors: string[];
}

export const UNKNOWN_VALUE = 'Unknown';

export function isUnknownValue(val: string | null | undefined): boolean {
    if (!val) return false;
    return val.trim().toLowerCase() === 'unknown';
}

/**
 * Returns true if a field was at its "untouched" default state before autogenerate ran,
 * meaning it was eligible to be overwritten by the AI.
 * Mirrors the logic in ApplicationManagerContent.tsx that decides whether to show
 * the Auto-Generate Responses section.
 */
function wasFieldEligibleForAutogenerate(beforeValue: string | undefined, associatedVariable: string | undefined, defaultValue: string | undefined): boolean {
    if (!beforeValue) return true;
    if (associatedVariable && beforeValue === associatedVariable) return true;
    if (defaultValue && beforeValue === defaultValue) return true;
    return false;
}

/**
 * Given the before/after applied template snapshots, returns which fields were
 * targeted by the autogenerate call and whether each was successfully generated.
 *
 * Only includes fields that were eligible (still at their default or associated-variable
 * value before the call). Fields already filled in by the user are excluded.
 */
export function computeFieldResults(
    beforeTemplates: AppliedApplicationTemplateModel[],
    result: IResult<AppliedApplicationTemplateModel[]> | undefined
): AutoGenerateResults {
    if (!result) {
        return {
            successFields: [],
            failedFields: [],
            hasApiError: true,
            apiErrors: ['An unexpected error occurred while generating field values.']
        };
    }

    if (result.resultType !== 'Ok') {
        return {
            successFields: [],
            failedFields: [],
            hasApiError: true,
            apiErrors: result.errors?.length > 0
                ? result.errors
                : ['The auto-generate request failed. Please try again.']
        };
    }

    const afterTemplates = result.data ?? [];
    const successFields: FieldGenerateResult[] = [];
    const failedFields: FieldGenerateResult[] = [];

    // Track (appliedTemplateId, fieldId) pairs to prevent double-counting if the API
    // returns the same template more than once or a template contains duplicate field entries.
    const seenKeys = new Set<string>();

    for (const afterTemplate of afterTemplates) {
        const beforeTemplate = beforeTemplates.find(t => t.id === afterTemplate.id);
        const afterFieldValues = afterTemplate.templateFormFieldValues ?? [];

        for (const afterField of afterFieldValues) {
            const fieldId = afterField.templateFormFieldId;

            // Deduplicate: skip if this (template, field) combination was already processed
            const dedupeKey = `${afterTemplate.id ?? ''}::${fieldId ?? afterField.dynamicFormFieldName ?? ''}`;
            if (seenKeys.has(dedupeKey)) continue;
            seenKeys.add(dedupeKey);

            const beforeField = beforeTemplate?.templateFormFieldValues?.find(f =>
                fieldId
                    ? f.templateFormFieldId === fieldId
                    : f.dynamicFormFieldName === afterField.dynamicFormFieldName
            );

            const beforeValue = beforeField?.value;
            const associatedVariable = afterField.associatedVariable ?? beforeField?.associatedVariable;
            const defaultValue = afterField.defaultValue ?? beforeField?.defaultValue;

            // Skip fields that already had user-entered content before the call
            if (!wasFieldEligibleForAutogenerate(beforeValue, associatedVariable, defaultValue)) {
                continue;
            }

            const displayTitle = afterField.title ?? afterField.label ?? afterField.dynamicFormFieldName ?? fieldId ?? 'Unknown Field';
            const generatedValue = afterField.value ?? '';
            const appName = afterTemplate.name ?? undefined;

            if (generatedValue && !isUnknownValue(generatedValue)) {
                successFields.push({ fieldTitle: displayTitle, generatedValue, appName });
            } else {
                failedFields.push({ fieldTitle: displayTitle, generatedValue: UNKNOWN_VALUE, appName });
            }
        }
    }

    return {
        successFields,
        failedFields,
        hasApiError: false,
        apiErrors: []
    };
}
